import { PortalAllMediaClasses } from './portal-theme';
import { TailwindClassName } from './tailwind-class-name';
import { TailwindPalette } from './tailwind-palette';

export type PortalGradientColor = [TailwindPalette, (TailwindPalette | TailwindClassName[])?, TailwindClassName[]?];

export type PortalSolidColor = [TailwindPalette];

export enum PortalBorderType {
  PRIMARY = 'primary',
  PRIMARY_HOVER = 'outline_primary_hover',
  FOOTER = 'outline_footer',
  SURFACE = 'outline_surface',
  SURFACE_DISABLE = 'outline_surface_disable',
  SURFACE_HOVER = 'outline_surface_hover',
  SURFACE_VARIANT = 'outline_surface_variant',
  SURFACE_INNER = 'outline_surface_inner',
  SURFACE_INNER_VARIANT = 'outline_surface_inner_variant',
  SURFACE_VARIANT_HOVER = 'outline_surface_variant_hover',
  SURFACE_INNER_HOVER = 'outline_surface_inner_bg_hover',
  EDIT_BORDER = 'edit_border',
}

export type PortalBorderColors = {
  [key in PortalBorderType]: PortalSolidColor;
};

export type PortalBorderOrColor = PortalBorderType | PortalSolidColor;

export enum PortalShadowType {
  MAIN = 'shadow_main',
  HOVER = 'shadow_hover',
  SEARCH = 'shadow_search',
}

export type PortalShadowColors = {
  [key in PortalShadowType]: PortalSolidColor;
};

export enum PortalBgType {
  BODY = 'bg_body',
  HEADER = 'bg_header',
  HEADER_MENU = 'header_menu',
  HEADER_LINKS = 'header_links',
  FOOTER = 'bg_footer',
  PRIMARY = 'bg_primary',
  SECONDARY = 'bg_secondary',
  SURFACE = 'bg_surface',
  SURFACE_DISABLE = 'bg_surface_disable',
  SURFACE_TEXT = 'bg_surface_text',
  TOOLTIP = 'bg_tooltip',
  SURFACE_VARIANT = 'bg_surface_variant',
  SURFACE_INNER = 'bg_surface_inner',
  SURFACE_INNER_VARIANT = 'bg_surface_inner_variant',
  SURFACE_INNER_VARIANT_2 = 'bg_surface_inner_variant_2',
  HEADER_HOVER = 'bg_header_hover',
  BODY_HOVER = 'bg_body_hover',
  SURFACE_VARIANT_HOVER = 'bg_surface_variant_hover',
  SURFACE_INNER_VARIANT_HOVER_2 = 'bg_surface_inner_variant_hover_2',
  SURFACE_INNER_HOVER = 'bg_surface_inner_hover',
  SURFACE_INNER_HOVER_2 = 'bg_surface_inner_hover_2',
  PRIMARY_HOVER = 'bg_primary_hover',
  SECONDARY_HOVER = 'bg_secondary_hover',
  FOOTER_LINE = 'bg_footer_line',
  FOOTER_DIVIDER = 'footer_divider',
  FILL_SURFACE = 'bg_fill_surface',
  FILL_SURFACE_2 = 'bg_fill_surface_2',
  FOOTER_LINKS = 'footer_links',
  FOOTER_LINKS_HOVER = 'footer_links_hover',
  GREETING_1 = 'bg_greeting_1',
  GREETING_2 = 'bg_greeting_2',
  EDIT_BG = 'edit_bg',
  WARN_SURFACE = 'warn_surface',
}
export type PortalBgColors = {
  [key in PortalBgType]: PortalGradientColor;
};
export type PortalBgOrColor = PortalBgType | PortalGradientColor;

export enum PortalTextType {
  ON_PRIMARY = 'text_on_primary',
  HERO_TEXT = 'hero_text',
  SURFACE = 'text_surface',
  SURFACE_DISABLE = 'text_surface_disable',
  SURFACE_2 = 'text_surface_2',
  SURFACE_3 = 'text_surface_3',
  SURFACE_VARIANT = 'text_surface_variant',
  SURFACE_VARIANT_2 = 'text_surface_variant_2',
  SURFACE_INNER = 'text_surface_inner',
  SURFACE_INNER_2 = 'text_surface_inner_2',
  SURFACE_INNER_VARIANT = 'text_surface_inner_variant',
  SURFACE_INNER_VARIANT_2 = 'text_surface_inner_variant_2',
  SECONDARY = 'text_secondary',
  HEADER = 'text_header',
  HEADER_HOVER = 'text_header_hover',
  BODY = 'text_body',
  BODY_VARIANT = 'text_body_variant',
  FOOTER = 'text_footer',
  FOOTER_2 = 'text_footer_2',
  FOOTER_HOVER = 'text_footer_hover',
  TOOLTIP = 'text_tooltip',
  PRIMARY = 'text_primary',
  PRIMARY_HOVER = 'text_primary_hover',
  EDIT_TEXT = 'edit_text',
}
export type PortalTextColors = {
  [key in PortalTextType]: PortalSolidColor;
};
export type PortalTextOrColor = PortalTextType | PortalSolidColor;

export interface PortalThemeImage {
  url: string;
  minHeight?: number;
  repeat?: 'repeat' | 'no-repeat' | 'repeat-x';
  position?: 'top' | 'bottom' | 'center' | 'right' | 'right-top' | 'right-bottom' | 'left' | 'left-top' | 'left-bottom';
  size?: 'auto' | 'cover' | 'contain';
  smooth?: boolean;
}

export type PortalThemeImageSet = {
  enabled: boolean;
  classNames?: TailwindClassName[];
  // [PortalAllMediaClasses.BASE]: PortalThemeImage;
} & {
  [key in PortalAllMediaClasses]?: PortalThemeImage;
};

export type PortalThemeLogoKeys = 'main' | 'footer';

export type PortalThemeLogoSet = {
  main: PortalThemeImage;
} & {
  [key in Exclude<PortalThemeLogoKeys, 'header'>]?: PortalThemeImage;
};

export type PortalIconSetName = 'default' | 'soft';

export enum EditorColorScheme {
  LIGHT = 'light',
  DARK = 'dark',
}
export interface PortalThemeTemplate {
  globalId: string;
  title: string;
  templateKey: PortalThemeKeys;
  portalId: string;
  previewUrl: string;
  published: boolean;
}
export type PortalThemeKeys =
  | 'webnotes_light'
  | 'webnotes_dark'
  | 'light_purple'
  | 'experimental'
  | 'experimental_2'
  | 'light_orange'
  | 'light_blue'
  | 'space_gray'
  | 'dark_night'
  | 'wedding_1'
  | 'wedding_2'
  | 'blue_gradient'
  | 'shades_of_green'
  | 'sketchy_digital'
  | 'lemon_drop'
  | 'ultramarine'
  | 'quite_green'
  | 'oxford'
  | 'carbon'
  | 'milky_blue'
  | 'soft_light'
  | 'savvy_red'
  | 'the_fusebase';

export interface PortalThemeVariantRules {
  path?: string;
  notPath?: string;
}
interface PortalThemeVariant {
  rules: PortalThemeVariantRules;
  modify: Partial<Omit<PortalThemeColors, 'key' | 'name' | 'hidden' | 'variants' | 'coomingSoon'>>;
}
export interface PortalThemeColors extends PortalBgColors, PortalTextColors, PortalShadowColors, PortalBorderColors {
  key: PortalThemeKeys;
  iconSet: PortalIconSetName;
  icon: string;
  name: string;
  thumbnail: string;
  preview: string;
  description: string;
  logo: string | PortalThemeLogoSet;
  editorColor: EditorColorScheme;
  comingSoon?: boolean;
  hidden?: boolean;
  imagesBg?: PortalThemeImageSet;
  imagesHead?: PortalThemeImageSet;
  variants?: PortalThemeVariant[];
}

export enum PortalColorSchemas {
  LIGHT = 'light',
  DARK = 'dark',
}
